"use client";

import { supabase } from "@/lib/supabase";
import type {
  ContainerFile,
  ContainerListResponse,
  ContainerReadResponse,
  ContainerStatus,
} from "@/types/container-files";
import { useCallback, useEffect, useRef, useState } from "react";

export type UseContainerFilesReturn = {
  files: ContainerFile[];
  status: ContainerStatus;
  loading: boolean;
  error: string | null;
  listedAt: string | null;
  usableUntil: string | null;
  refresh: () => Promise<void>;
  readFile: (path: string) => Promise<string>;
  downloadFile: (path: string) => Promise<Blob>;
};

async function authedResponse(body: unknown): Promise<Response> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Please sign in.");

  const res = await fetch("/api/chat/container-files", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const payload = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || "The sandbox request failed.");
  }
  return res;
}

async function authedPost<T>(body: unknown): Promise<T> {
  const res = await authedResponse(body);
  const payload = await res.json().catch(() => null) as T | null;
  if (!payload) throw new Error("The sandbox returned nothing.");
  return payload;
}

/**
 * Lists and reads files in the chat's code-execution sandbox.
 *
 * Anthropic uses a small model round-trip; OpenAI uses its container file API.
 * This never polls. It loads once per chat/provider and afterwards when the user
 * asks, which is also why the panel shows `listedAt`: a listing is a snapshot,
 * not a live view.
 */
export function useContainerFiles(chatId: string | null, provider: "anthropic" | "openai" = "anthropic", enabled = true): UseContainerFilesReturn {
  const [files, setFiles] = useState<ContainerFile[]>([]);
  const [status, setStatus] = useState<ContainerStatus>("none");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listedAt, setListedAt] = useState<string | null>(null);
  const [usableUntil, setUsableUntil] = useState<string | null>(null);
  const [loadedScope, setLoadedScope] = useState<string | null>(null);

  // Guards against a slow listing for a previous chat landing on a new one.
  const requestedChatRef = useRef<string | null>(null);

  const load = useCallback(async (id: string) => {
    const requestKey = `${id}:${provider}`;
    requestedChatRef.current = requestKey;
    setLoading(true);
    setError(null);
    try {
      const data = await authedPost<ContainerListResponse>({ chatId: id, action: "list", provider });
      if (requestedChatRef.current !== requestKey) return;
      setFiles(data.files ?? []);
      setStatus(data.status ?? "none");
      setListedAt(data.listedAt ?? null);
      setUsableUntil(data.usableUntil ?? null);
      setLoadedScope(requestKey);
    } catch (err: unknown) {
      if (requestedChatRef.current !== requestKey) return;
      console.error("[useContainerFiles] list failed:", err);
      setError(err instanceof Error ? err.message : "Failed to list sandbox files.");
    } finally {
      if (requestedChatRef.current === requestKey) setLoading(false);
    }
  }, [provider]);

  useEffect(() => {
    if (!enabled) {
      requestedChatRef.current = null;
      return;
    }
    if (!chatId) {
      requestedChatRef.current = null;
      setFiles([]);
      setStatus("none");
      setListedAt(null);
      setUsableUntil(null);
      setError(null);
      return;
    }
    void load(chatId);
  }, [chatId, load, enabled]);

  const refresh = useCallback(async () => {
    if (chatId) await load(chatId);
  }, [chatId, load]);

  const readFile = useCallback(
    async (path: string) => {
      if (!chatId) throw new Error("No chat is open.");
      const data = await authedPost<ContainerReadResponse>({
        chatId,
        action: "read",
        path,
        provider,
      });
      if (data.usableUntil && requestedChatRef.current === `${chatId}:${provider}`) setUsableUntil(data.usableUntil);
      return data.content ?? "";
    },
    [chatId, provider],
  );

  const downloadFile = useCallback(async (path: string) => {
    if (!chatId) throw new Error("No chat is open.");
    const response = await authedResponse({ chatId, action: "download", path, provider });
    if (provider === "openai" && requestedChatRef.current === `${chatId}:${provider}`) {
      setUsableUntil(new Date(Date.now() + 20 * 60_000).toISOString());
    }
    return response.blob();
  }, [chatId, provider]);

  const isCurrentScope = loadedScope === `${chatId}:${provider}`;
  return {
    files: isCurrentScope ? files : [],
    status: isCurrentScope ? status : "none",
    loading,
    error,
    listedAt: isCurrentScope ? listedAt : null,
    usableUntil: isCurrentScope ? usableUntil : null,
    refresh,
    readFile,
    downloadFile,
  };
}
